import { useState } from "react";
import { useParams } from "react-router-dom";

import { useScanPackages, useScans } from "../api/queries";
import "./pages.css";
import { Empty } from "../components/Empty";
import { ErrorBand } from "../components/ErrorBand";
import { LotsTable } from "../components/LotsTable";
import { Panel } from "../components/Panel";
import { Skeleton } from "../components/Skeleton";
import { TitleBand } from "../components/TitleBand";
import type { LotSort } from "./offersSort";
import { filterLots, sortLots } from "./offersSort";
import { scanWalked } from "./scanRoute";

const SORTS: LotSort[] = ["trader", "total"];
const SORT_LABELS: Record<LotSort, string> = { trader: "trader", total: "lot price" };

/** A package is not an offer row, which is why this page reads /packages rather
 *  than /offers?mode=package: the lot has one price between its items, and the
 *  offers table can only show that as the repeated total it is stored as. */
export function Packages() {
  const { uid = "" } = useParams();
  const [sort, setSort] = useState<LotSort>("trader");
  const [filter, setFilter] = useState("");

  const scans = useScans();
  const packages = useScanPackages(uid);
  const scan = scans.data?.find((s) => s.scan_uid === uid);

  if (packages.isError) {
    return (
      <>
        <TitleBand title="Package sales" />
        <ErrorBand
          message="Could not load this scan's package sales."
          hint="The server may be restarting. Reload in a moment."
        />
      </>
    );
  }
  if (!packages.data) {
    return <><TitleBand title="Package sales" /><Skeleton rows={6} /></>;
  }

  const lots = sortLots(filterLots(packages.data, filter), sort);
  const needle = filter.trim();

  return (
    <>
      <TitleBand
        title="Package sales"
        note={`${lots.length} ${lots.length === 1 ? "lot" : "lots"}, sold whole`}
      />
      <Panel primary>
        <div className="strip">
          <span>Sort</span>
          {SORTS.map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={sort === option}
              onClick={() => setSort(option)}
            >
              {SORT_LABELS[option]}
            </button>
          ))}
          <span style={{ flex: 1 }} />
          <input
            aria-label="Filter items"
            placeholder="filter item…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>

        {lots.length === 0 ? (
          needle ? (
            <Empty>Nothing matching “{needle}”.</Empty>
          ) : scanWalked(scan, "packages") ? (
            <Empty>No package sales in this scan.</Empty>
          ) : (
            <Empty>
              This scan didn’t walk packages. Run a scan with package enabled to
              see them here.
            </Empty>
          )
        ) : (
          <LotsTable lots={lots} />
        )}
      </Panel>
    </>
  );
}
